import {
    Box,
    Button,
    Card,
    CardContent,
    CardActions,
    // Collapse,
    Menu,
    MenuItem,
    Divider,
    Avatar,
    IconButton,
    Typography,
    styled,
    Stack,
    Tooltip,
} from '@mui/material';
import { useEffect, useState } from 'react';
import { useMutation, useQueryClient } from 'react-query';
import dayjs from 'dayjs';
import { useSnackbar } from 'notistack';
import LoadingWrapper from 'src/components/Loading/LoadingWrapper';
import MessagesService from 'src/services/MessagesService';
import useMessagesStore from 'src/stores/messages';
import { useClipboard } from 'src/hooks/useClipboard';
import ArrowUpRightInCircle from 'src/icons/ArrowUpRightInCircle';
import More from 'src/icons/More';
import Bookmark from 'src/icons/Bookmark';
import Checked from 'src/icons/Checked';
import Copy from 'src/icons/Copy';
import TrashIcon from 'src/icons/TrashIcon';

// import CollapseIcon from 'src/icons/CollapseIcon';

import { AIIcon } from 'src/icons/AIIcon';

import { getTgNickNameByLink } from '../utils';
import { getFeedbackLabel, getSavedFeedback, writeFeedback } from './leadFeedback';

import type { MouseEvent } from 'react';
import type { IMessage } from 'src/types/Message';
import type { LeadFeedback } from './leadFeedback';

type Props = {
    body: IMessage;
    checkbox?: React.ReactNode;
    onDeletingStart?: (messageId: number) => void;
    onDeletingEnd?: (messageId: number) => void;
};

const MessageBox = styled(Box)(() => ({
    padding: '4px 8px',
    borderRadius: '8px',
    boxShadow: '0px 2px 10px 0px rgba(68, 73, 103, 0.10)',
}));

const TimeBox = styled(Box)(() => ({
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    width: 30,
    flexShrink: 0,
    flexGrow: 0,
}));

const CardHeader = styled(Box)(() => ({}));

const FeedbackActionButton = styled(IconButton, {
    shouldForwardProp: (prop) => prop !== 'feedbackTone' && prop !== 'isActive',
})<{ feedbackTone: 'good' | 'bad'; isActive: boolean }>(({ feedbackTone, isActive }) => {
    const isGood = feedbackTone === 'good';
    const activeColor = isGood ? '#16794C' : '#B42336';
    const activeBackground = isGood ? '#E8F8EF' : '#FFF1F3';
    const activeBorder = isGood ? '#74C69D' : '#E88998';
    return {
        width: 34,
        height: 34,
        border: `1px solid ${isActive ? activeBorder : '#E3ECF3'}`,
        borderRadius: '8px',
        color: isActive ? activeColor : '#7A8699',
        backgroundColor: isActive ? activeBackground : '#FFFFFF',
        padding: 0,
        '&:hover': {
            borderColor: isActive ? activeBorder : isGood ? '#BFE8D1' : '#F3C6CD',
            backgroundColor: isActive ? activeBackground : isGood ? '#F0FFF6' : '#FFF5F6',
        },
    };
});

const BadFeedbackIcon = styled('span')(() => ({
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 20,
    lineHeight: '14px',
    width: 18,
}));

const getCardFeedbackSx = (feedback: LeadFeedback) => {
    if (feedback === 'good') {
        return { backgroundColor: '#F2FBF6', borderColor: '#BFE8D1' };
    }
    if (feedback === 'bad') {
        return { backgroundColor: '#F3F4F6', borderColor: '#D9DEE7', opacity: 0.72 };
    }
    return {};
};

const getInitialFeedback = (message: IMessage): LeadFeedback =>
    typeof message.feedback === 'undefined' ? getSavedFeedback(message.id) : message.feedback;

export default ({ body, checkbox, onDeletingStart, onDeletingEnd }: Props) => {
    const { enqueueSnackbar } = useSnackbar();
    const queryClient = useQueryClient();
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const [isRemoved, setIsRemoved] = useState(false); // Локальное состояние для немедленного скрытия
    const [feedback, setFeedback] = useState<LeadFeedback>(() => getInitialFeedback(body));
    const { copy } = useClipboard();
    const open = Boolean(anchorEl);
    const isFavoriteOnly = false; // Убираем проверку на отдельную страницу favorites
    const feedbackLabel = getFeedbackLabel(feedback);

    const handleClose = () => {
        setAnchorEl(null);
    };

    const { isLoading, mutateAsync } = useMutation(useMessagesStore.getState().toggleFavorite, {
        onSuccess: (_, variables) => {
            const isAdding = variables.flag;
            enqueueSnackbar(isAdding ? 'Сообщение добавлено в избранное' : 'Сообщение удалено из избранного', {
                variant: 'success',
            });
            handleClose(); // Закрываем меню после успешного изменения
            // Инвалидируем кеш запросов сообщений для обновления списка
            queryClient.invalidateQueries(['fetch-message']);
        },
        onError: (error) => {
            console.log(error);
            enqueueSnackbar('Произошла ошибка. Попробуйте повторить позже', { variant: 'error' });
        },
    });

    const { isLoading: isLoadingRemove, mutateAsync: removeMessage } = useMutation(
        useMessagesStore.getState().removeMessage,
        {
            onMutate: async (messageId) => {
                setIsRemoved(true);
                onDeletingStart?.(messageId);

                await queryClient.cancelQueries({ queryKey: ['fetch-message'] });

                const previousQueries = queryClient.getQueriesData({ queryKey: ['fetch-message'] });
                const previousStoreState = useMessagesStore.getState();

                // Оптимистично обновляем кеш React Query - удаляем сообщение из всех страниц
                previousQueries.forEach(([queryKey, oldData]) => {
                    if (!oldData || !(oldData as any).pages) return;

                    queryClient.setQueryData(queryKey, (old: any) => {
                        if (!old || !old.pages) return old;

                        const updatedPages = old.pages.map((page: any) => ({
                            ...page,
                            results: (page.results || []).filter((msg: IMessage) => msg.id !== messageId),
                        }));

                        return {
                            ...old,
                            pages: updatedPages,
                            // Обновляем count в первой странице
                            ...(updatedPages[0] && {
                                pages: updatedPages.map((page: any, index: number) =>
                                    index === 0 ? { ...page, count: Math.max(0, (page.count || 0) - 1) } : page
                                ),
                            }),
                        };
                    });
                });

                // Оптимистично обновляем store
                const updatedMessages = previousStoreState.messages.filter((msg) => msg.id !== messageId);
                const newCount =
                    previousStoreState.count > 0 ? Math.max(0, previousStoreState.count - 1) : previousStoreState.count;

                useMessagesStore.setState({
                    messages: updatedMessages,
                    count: newCount,
                });

                return { previousQueries, previousStoreState, messageId };
            },
            onSuccess: (_, messageId) => {
                enqueueSnackbar('Сообщение успешно удалено', { variant: 'success' });
                handleClose();
                onDeletingEnd?.(messageId);
                // НЕ инвалидируем кеш - оптимистичное обновление уже сделано
                // Инвалидация может вызвать повторную загрузку и дубли
            },
            onError: (error, messageId, context) => {
                console.log(error);
                setIsRemoved(false);
                onDeletingEnd?.(messageId);

                if (context?.previousStoreState) {
                    useMessagesStore.setState({
                        messages: context.previousStoreState.messages,
                        count: context.previousStoreState.count,
                    });
                }

                if (context?.previousQueries) {
                    context.previousQueries.forEach(([queryKey, data]) => {
                        queryClient.setQueryData(queryKey, data);
                    });
                }

                enqueueSnackbar('Неудалось удалить сообщение. Попробуйте повторить позже', { variant: 'error' });
            },
        }
    );

    const syncFeedbackInQueryCache = (messageId: number, nextFeedback: LeadFeedback) => {
        const previousQueries = queryClient.getQueriesData({ queryKey: ['fetch-message'] });
        previousQueries.forEach(([queryKey]) => {
            queryClient.setQueryData(queryKey, (oldData: any) => {
                if (!oldData?.pages) return oldData;
                return {
                    ...oldData,
                    pages: oldData.pages.map((page: any) => ({
                        ...page,
                        results: (page.results || []).map((message: IMessage) =>
                            message.id === messageId ? { ...message, feedback: nextFeedback } : message
                        ),
                    })),
                };
            });
        });
        return previousQueries;
    };

    const { isLoading: isFeedbackSaving, mutateAsync: saveFeedback } = useMutation(
        ({ id, nextFeedback }: { id: number; nextFeedback: LeadFeedback }) =>
            MessagesService.saveMessageFeedback(id, nextFeedback),
        {
            onMutate: async ({ id, nextFeedback }) => {
                await queryClient.cancelQueries({ queryKey: ['fetch-message'] });
                const previousFeedback = feedback;
                const previousQueries = syncFeedbackInQueryCache(id, nextFeedback);
                setFeedback(nextFeedback);
                writeFeedback(id, nextFeedback);
                useMessagesStore.getState().setFeedback(id, nextFeedback);
                return { previousFeedback, previousQueries };
            },
            onSuccess: (savedFeedback, variables) => {
                setFeedback(savedFeedback);
                writeFeedback(variables.id, savedFeedback);
                syncFeedbackInQueryCache(variables.id, savedFeedback);
                useMessagesStore.getState().setFeedback(variables.id, savedFeedback);
                enqueueSnackbar(
                    savedFeedback ? getFeedbackLabel(savedFeedback) || 'Оценка сохранена' : 'Оценка снята',
                    {
                        variant: savedFeedback === 'bad' ? 'info' : 'success',
                    }
                );
            },
            onError: (_error, variables, context) => {
                const previousFeedback = context?.previousFeedback ?? null;
                setFeedback(previousFeedback);
                writeFeedback(variables.id, previousFeedback);
                useMessagesStore.getState().setFeedback(variables.id, previousFeedback);
                context?.previousQueries?.forEach(([queryKey, data]) => queryClient.setQueryData(queryKey, data));
                enqueueSnackbar('Не удалось сохранить оценку лида', { variant: 'error' });
            },
        }
    );

    const setFavoriteStatus = async () => {
        try {
            await mutateAsync({ id: body.id, flag: !body.favorite });

            // Если сообщение было в избранном и мы находимся в режиме только избранного,
            // удаляем его из списка
            if (!!body.favorite && isFavoriteOnly) {
                useMessagesStore.getState().deleteFromFavorite(body.id);
            }
        } catch (error) {
            // Ошибка уже обработана в onError
        }
    };

    const handleClickCardMenu = (event: MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handleFavoriteClick = () => {
        if (!isLoading) {
            setFavoriteStatus();
        }
    };

    const handleRemove = () => {
        if (!isLoadingRemove) {
            removeMessage(body.id).catch(() => {
                // Ошибка уже обработана в onError
            });
        }
    };

    const handleFeedback = (value: Exclude<LeadFeedback, null>) => {
        if (isFeedbackSaving) return;
        saveFeedback({ id: body.id, nextFeedback: feedback === value ? null : value }).catch(() => {});
    };

    useEffect(() => {
        setIsRemoved(false);
        setFeedback(getInitialFeedback(body));
    }, [body.id, body.feedback]);

    const renderContextMenuCard = () => {
        return (
            <Menu
                anchorEl={anchorEl}
                id="card-menu"
                open={open}
                onClose={handleClose}
                onClick={handleClose}
                //disable lock for window
                //disableScrollLock
                // marginThreshold={null}
            >
                <Box
                    width={240}
                    sx={{
                        background: '#EBF0F2',
                    }}
                    mt={-1}
                    p={0.5}
                >
                    <Box
                        display="flex"
                        flexDirection="column"
                        alignItems="center"
                        justifyContent="center"
                        width={1}
                        height={54}
                        p={1}
                        borderRadius={3}
                        onClick={handleFavoriteClick}
                        style={{
                            cursor: 'pointer',
                        }}
                        bgcolor="background.default"
                    >
                        <Bookmark fill={body.favorite} />
                        <Typography variant="overline">Добавить в избранное</Typography>
                    </Box>
                </Box>
                <MenuItem
                    onClick={() => {
                        copy(body.link);
                    }}
                >
                    Скопировать ссылку <Copy />
                </MenuItem>
                {/* Удаление доступно только для сообщений из мониторинга */}
                {body.source !== 'stream' && (
                    <>
                        <Divider />
                        <MenuItem
                            disabled={isLoadingRemove}
                            onClick={handleRemove}
                        >
                            Удалить сообщение <TrashIcon />
                        </MenuItem>
                    </>
                )}
            </Menu>
        );
    };
    const renderTitle = () => {
        const isFromStream = body.source === 'stream' && body.stream_name;
        const sourceName = isFromStream ? body.stream_name : body.chat_name;

        return (
            <Box
                display="flex"
                alignItems="center"
                gap={1}
                maxWidth={'80%'}
                overflow="hidden"
            >
                <Typography
                    variant="body2"
                    noWrap
                    title={sourceName}
                    sx={{ fontWeight: 600 }}
                >
                    {sourceName}
                </Typography>
                {sourceName && (
                    <Typography
                        variant="body2"
                        color="textDisabled"
                        sx={{ fontWeight: 300 }}
                    >
                        от
                    </Typography>
                )}
                <Typography
                    variant="body2"
                    noWrap
                    title={body.author}
                    sx={{ fontWeight: 400 }}
                >
                    {getTgNickNameByLink(body.author)}
                </Typography>
            </Box>
        );
    };

    const renderMessage = () => {
        // Явно проверяем relevance === true
        const isRelevant = body.relevance === true;

        return (
            <>
                <Box
                    display="flex"
                    gap={1}
                    paddingRight={1}
                >
                    {isRelevant && (
                        <Box
                            display="block"
                            width={1}
                            sx={{
                                borderRadius: '8px',
                                boxShadow:
                                    ' 0px -2px 6px 0px rgba(0, 163, 255, 0.20), 0px 2px 6px 0px rgba(250, 0, 255, 0.10);',
                            }}
                        >
                            <MessageBox>
                                <Typography sx={{ fontWeight: 400 }}>{body.content}</Typography>
                            </MessageBox>
                            {/* тут будет раскрывашка  */}
                        </Box>
                    )}

                    {!isRelevant && (
                        <Box
                            display="block"
                            width={1}
                            sx={{
                                borderRadius: '8px',
                            }}
                        >
                            <MessageBox>
                                <Typography>{body.content}</Typography>
                            </MessageBox>
                            {/* тут будет раскрывашка  */}
                        </Box>
                    )}

                    <TimeBox>
                        <Typography
                            variant="overline"
                            color="textDisabled"
                            sx={{ fontWeight: 300 }}
                        >
                            {dayjs(body.date).format('HH:mm')}
                        </Typography>
                        {/* <IconButton size="small">
                        <CollapseIcon />
                    </IconButton> */}
                    </TimeBox>
                </Box>

                {isRelevant && (
                    <Stack
                        gap="2px"
                        direction="row"
                        alignItems="center"
                        sx={{ mt: 0.5 }}
                    >
                        <AIIcon />
                        <Typography
                            variant="body2"
                            sx={{
                                background: 'linear-gradient(90deg, #FF88C0, #9FA9BC)',
                                WebkitBackgroundClip: 'text',
                                color: 'transparent',
                            }}
                        >
                            Целевое сообщение
                        </Typography>
                    </Stack>
                )}
            </>
        );
    };
    // Если сообщение удалено, не рендерим карточку
    if (isRemoved) {
        return null;
    }

    return (
        <LoadingWrapper
            isLoading={false}
            isLocal
        >
            {renderContextMenuCard()}
            <Card
                variant="outlined"
                sx={getCardFeedbackSx(feedback)}
            >
                <Box
                    display="flex"
                    alignItems="center"
                    px={1}
                    sx={{ mt: 1.5 }}
                >
                    {checkbox && (
                        <Box
                            display="flex"
                            alignItems="center"
                            justifyContent="center"
                            minWidth={40}
                        >
                            {checkbox}
                        </Box>
                    )}
                    <Box flex={1}>{renderTitle()}</Box>
                    <Box
                        display="flex"
                        alignItems="center"
                        gap={1}
                    >
                        <Typography
                            variant="body2"
                            color="textDisabled"
                            flex={1}
                            noWrap
                        >
                            {(() => {
                                const ago = dayjs(body.date).fromNow(true);
                                if (ago === 'час') return '1 час';
                                return ago;
                            })()}
                        </Typography>
                    </Box>
                </Box>
                <CardContent sx={{ width: '100%' }}>{renderMessage()}</CardContent>
                <CardActions
                    style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                    }}
                    disableSpacing
                >
                    <Button
                        variant="outlined"
                        size="medium"
                        color="secondary"
                        sx={{
                            fontSize: '14px',
                            border: '1px solid #E3ECF3',
                            borderRadius: '8px',
                            fontWeight: 400,
                        }}
                        startIcon={<ArrowUpRightInCircle />}
                        onClick={() => {
                            window.open(body.author, '_blank');
                        }}
                    >
                        Написать
                    </Button>
                    <Box
                        display="flex"
                        alignItems="center"
                        gap={0.5}
                    >
                        {feedbackLabel && (
                            <Typography
                                variant="body2"
                                sx={{
                                    borderRadius: '999px',
                                    bgcolor: feedback === 'good' ? '#E8F8EF' : '#E5E7EB',
                                    color: feedback === 'good' ? '#16794C' : '#667085',
                                    fontSize: '12px',
                                    fontWeight: 600,
                                    mr: 0.5,
                                    px: 1,
                                    py: 0.25,
                                    whiteSpace: 'nowrap',
                                }}
                            >
                                {feedbackLabel}
                            </Typography>
                        )}
                        <Tooltip title="Хороший лид">
                            <FeedbackActionButton
                                feedbackTone="good"
                                isActive={feedback === 'good'}
                                aria-label="Хороший лид"
                                disabled={isFeedbackSaving}
                                onClick={() => handleFeedback('good')}
                            >
                                <Checked
                                    fillColor={feedback === 'good' ? '#24A866' : 'currentColor'}
                                    fontColor="#FFFFFF"
                                />
                            </FeedbackActionButton>
                        </Tooltip>
                        <Tooltip title="Не подходит">
                            <FeedbackActionButton
                                feedbackTone="bad"
                                isActive={feedback === 'bad'}
                                aria-label="Не подходит"
                                disabled={isFeedbackSaving}
                                onClick={() => handleFeedback('bad')}
                            >
                                <BadFeedbackIcon>×</BadFeedbackIcon>
                            </FeedbackActionButton>
                        </Tooltip>
                        <IconButton
                            size="medium"
                            onClick={() => {
                                copy(body.link);
                                enqueueSnackbar('Ссылка скопирована', { variant: 'success' });
                            }}
                            sx={{ padding: 0.5 }}
                        >
                            <Copy />
                        </IconButton>
                        {/* Удаление доступно только для сообщений из мониторинга */}
                        {body.source !== 'stream' && (
                            <IconButton
                                size="medium"
                                onClick={handleRemove}
                                disabled={isLoadingRemove}
                                sx={{
                                    padding: 0.5,
                                    color: 'inherit',
                                    '&:hover': {
                                        color: 'rgba(228,63,63,0.8)',
                                    },
                                }}
                            >
                                <TrashIcon />
                            </IconButton>
                        )}
                        <IconButton
                            size="medium"
                            onClick={handleFavoriteClick}
                            disabled={isLoading}
                            sx={{
                                padding: 0.5,
                            }}
                        >
                            <Bookmark fill={body.favorite} />
                        </IconButton>
                    </Box>
                </CardActions>
            </Card>
        </LoadingWrapper>
    );
};
